import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Check, CheckCheck, Eye, Target, Calendar, MapPin, TrendingUp, DollarSign, Zap, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { guidanceAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import WatchButton from '../components/guidance/WatchButton';

const ALERT_ICONS = {
  funding_milestone: TrendingUp,
  new_event: Calendar,
  momentum: Zap,
  investment: DollarSign,
  new_project: Target,
  city_update: MapPin,
  default: Bell,
};

const ALERT_COLORS = {
  funding_milestone: 'text-emerald-400 bg-emerald-500/10',
  new_event: 'text-amber-400 bg-amber-500/10',
  momentum: 'text-violet-400 bg-violet-500/10',
  investment: 'text-green-400 bg-green-500/10',
  new_project: 'text-blue-400 bg-blue-500/10',
  city_update: 'text-cyan-400 bg-cyan-500/10',
  default: 'text-zinc-400 bg-zinc-800',
};

export default function AlertCenterPage() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const [watchlist, setWatchlist] = useState([]);
  const [tab, setTab] = useState('alerts');
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await guidanceAPI.getAlerts(100);
      setAlerts(res.data?.alerts || []);
      setUnread(res.data?.unread_count || 0);
    } catch {}
  }, []);

  const fetchWatchlist = useCallback(async () => {
    try {
      const res = await guidanceAPI.getWatchlist();
      setWatchlist(res.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    if (!user) return;
    Promise.all([fetchAlerts(), fetchWatchlist()]).finally(() => setLoading(false));
  }, [user, fetchAlerts, fetchWatchlist]);

  const markRead = async (id) => {
    await guidanceAPI.markAlertRead(id);
    setAlerts(prev => prev.map(a => a.id === id ? { ...a, read: true } : a));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await guidanceAPI.markAllRead();
    setAlerts(prev => prev.map(a => ({ ...a, read: true })));
    setUnread(0);
  };

  // Group alerts by date
  const grouped = {};
  alerts.forEach(a => {
    const day = a.created_at?.split('T')[0] || 'Unknown';
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push(a);
  });

  if (!user) return <div className="max-w-3xl mx-auto p-8 text-center text-zinc-500">Please sign in to view alerts</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8" data-testid="alert-center-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-2xl text-zinc-900">Alert Center</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {unread > 0 ? `${unread} unread alert${unread > 1 ? 's' : ''}` : 'All caught up'}
          </p>
        </div>
        {unread > 0 && (
          <Button
            onClick={markAllRead}
            variant="outline"
            className="text-xs border-zinc-300 text-zinc-600"
            data-testid="mark-all-read-btn"
          >
            <CheckCheck size={14} className="mr-1.5" /> Mark all read
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-zinc-200 pb-3">
        <button
          onClick={() => setTab('alerts')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            tab === 'alerts' ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-100'
          }`}
          data-testid="tab-alerts"
        >
          <Bell size={14} className="inline mr-1.5" />
          Alerts {unread > 0 && <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-[10px] rounded-full">{unread}</span>}
        </button>
        <button
          onClick={() => setTab('watchlist')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            tab === 'watchlist' ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:bg-zinc-100'
          }`}
          data-testid="tab-watchlist"
        >
          <Eye size={14} className="inline mr-1.5" />
          Watchlist ({watchlist.length})
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-zinc-100 rounded-xl animate-pulse" />)}
        </div>
      ) : tab === 'alerts' ? (
        alerts.length === 0 ? (
          <div className="text-center py-16 bg-zinc-50 rounded-2xl border border-zinc-200">
            <Bell size={32} className="text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500 font-medium">No alerts yet</p>
            <p className="text-zinc-400 text-sm mt-1">Watch artists, projects, or cities to get notified</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.entries(grouped).map(([date, items]) => (
              <div key={date}>
                <p className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3">{date}</p>
                <div className="space-y-2">
                  {items.map((alert, idx) => {
                    const Icon = ALERT_ICONS[alert.alert_type] || ALERT_ICONS.default;
                    const colors = ALERT_COLORS[alert.alert_type] || ALERT_COLORS.default;
                    return (
                      <motion.div
                        key={alert.id}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.03 }}
                        className={`flex items-start gap-3 p-4 rounded-xl border transition-all ${
                          alert.read ? 'bg-white border-zinc-200' : 'bg-zinc-50 border-zinc-300'
                        }`}
                        data-testid={`alert-item-${alert.id}`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${colors}`}>
                          <Icon size={16} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${alert.read ? 'text-zinc-600' : 'text-zinc-900'}`}>{alert.title}</p>
                          <p className="text-xs text-zinc-500 mt-0.5">{alert.message}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {alert.link && (
                            <Link to={alert.link} className="text-xs text-zinc-500 hover:text-zinc-800">
                              <ChevronRight size={14} />
                            </Link>
                          )}
                          {!alert.read && (
                            <button
                              onClick={() => markRead(alert.id)}
                              className="text-zinc-400 hover:text-emerald-500 transition-colors"
                              data-testid={`mark-read-${alert.id}`}
                            >
                              <Check size={14} />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        /* Watchlist Tab */
        watchlist.length === 0 ? (
          <div className="text-center py-16 bg-zinc-50 rounded-2xl border border-zinc-200">
            <Eye size={32} className="text-zinc-300 mx-auto mb-3" />
            <p className="text-zinc-500 font-medium">Your watchlist is empty</p>
            <p className="text-zinc-400 text-sm mt-1">Watch artists, projects, events, or cities to track them here</p>
            <div className="mt-4 flex justify-center gap-3">
              <Link to="/projects"><Button variant="outline" className="text-xs border-zinc-300" data-testid="browse-projects-from-watchlist">Browse Projects</Button></Link>
              <Link to="/events"><Button variant="outline" className="text-xs border-zinc-300" data-testid="browse-events-from-watchlist">Browse Events</Button></Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {watchlist.map((item, idx) => {
              const linkMap = {
                artist: `/artist/${item.entity_id}`,
                project: `/projects/${item.entity_id}`,
                event: `/events/${item.entity_id}`,
                city: `/local-scene?city=${encodeURIComponent(item.entity_id)}`,
              };
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                >
                  <Link
                    to={linkMap[item.entity_type] || '#'}
                    className="flex items-center gap-3 p-4 rounded-xl bg-white border border-zinc-200 hover:border-zinc-400 transition-all group"
                    data-testid={`watchlist-item-${item.entity_type}-${item.entity_id}`}
                  >
                    {item.image || item.avatar ? (
                      <img src={item.image || item.avatar} alt="" className="w-10 h-10 rounded-lg object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-zinc-100 flex items-center justify-center">
                        <MapPin size={16} className="text-zinc-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{item.name || item.entity_id}</p>
                      <p className="text-xs text-zinc-500">{item.entity_type}{item.genre ? ` · ${item.genre}` : ''}{item.funded_pct !== undefined ? ` · ${item.funded_pct}% funded` : ''}{item.date ? ` · ${item.date}` : ''}</p>
                    </div>
                    <WatchButton entityType={item.entity_type} entityId={item.entity_id} size="sm" />
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
}
